import { useEffect, useState } from "react";
import { Download, FileBadge, LockKeyhole, LogOut, ShieldCheck, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { PolicyPageShell } from "@/components/PolicyHeader";
import { CustomerLetters, customerSession } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;
const errMsg = (e, fallback) => e?.response?.data?.detail?.message_en || (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : fallback);
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");
const LOAN = { personal_loan: "Personal Loan", business_loan: "Business Loan", home_loan: "Home Loan", loan_against_property: "Loan Against Property" };

const Card = ({ children, testId }) => (
  <div data-testid={testId} className="relative rounded-2xl border border-[#D4AF37]/25 bg-white/[0.03] backdrop-blur-xl p-6 sm:p-8 shadow-2xl">{children}</div>
);
const Field = ({ label, children }) => (
  <label className="block text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]/90">{label}<div className="mt-2">{children}</div></label>
);
const inputCls = "w-full rounded-xl bg-[#0A1F44] border border-white/15 focus:border-[#D4AF37] outline-none px-4 min-h-[48px] text-white text-base tracking-wider placeholder:text-white/30 transition-colors";
const goldBtn = "inline-flex items-center justify-center gap-2 min-h-[48px] px-6 rounded-full bg-[#D4AF37] text-[#04102B] font-semibold text-sm hover:bg-[#F0D788] disabled:opacity-40 disabled:cursor-not-allowed transition-colors";

const MobileStep = ({ onSent }) => {
  const [mobile, setMobile] = useState("");
  const [busy, setBusy] = useState(false);
  const valid = /^[6-9]\d{9}$/.test(mobile);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const d = await CustomerLetters.requestOtp(mobile); toast.success(`OTP sent to ${d.mobile_masked}`); onSent(mobile, d); }
    catch (err) { toast.error(errMsg(err, "Could not send OTP")); }
    finally { setBusy(false); }
  };
  return (
    <Card testId="portal-mobile-step">
      <div className="flex items-center gap-3 text-white/85"><Smartphone size={18} className="text-[#D4AF37]" /><span className="text-sm">Step 1 · Registered mobile number</span></div>
      <form onSubmit={submit} className="mt-6 space-y-5">
        <Field label="Mobile number">
          <div className="flex items-stretch gap-2">
            <span className="inline-flex items-center px-3 rounded-xl border border-white/15 text-white/60 text-sm">+91</span>
            <input data-testid="portal-mobile-input" inputMode="numeric" autoComplete="tel-national" maxLength={10} value={mobile} placeholder="98765 43210"
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))} className={inputCls} />
          </div>
        </Field>
        <button data-testid="portal-request-otp" type="submit" disabled={!valid || busy} className={goldBtn}>{busy ? "Sending…" : "Send OTP"}</button>
        <p className="text-[12px] text-white/70 leading-relaxed">Use the mobile number you gave on your eligibility application. An OTP valid for 5 minutes will be sent via SMS. Maximum 3 OTP requests per 15 minutes.</p>
      </form>
    </Card>
  );
};

const OtpStep = ({ mobile, meta, onVerified, onBack, onResent }) => {
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [left, setLeft] = useState(meta?.expires_in_seconds || 300);
  const [cooldown, setCooldown] = useState(meta?.resend_after_seconds || 60);
  useEffect(() => { const t = setInterval(() => { setLeft((s) => Math.max(0, s - 1)); setCooldown((c) => Math.max(0, c - 1)); }, 1000); return () => clearInterval(t); }, []);
  const resend = async () => {
    setBusy(true);
    try { const d = await CustomerLetters.requestOtp(mobile); onResent(d); setLeft(d.expires_in_seconds); setCooldown(d.resend_after_seconds || 60); setOtp(""); toast.success("New OTP sent"); }
    catch (err) { toast.error(errMsg(err, "Could not resend OTP")); }
    finally { setBusy(false); }
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const d = await CustomerLetters.verifyOtp(mobile, otp); customerSession.set(d.session_token); onVerified(d); }
    catch (err) {
      const code = err?.response?.data?.detail?.code;
      toast.error(errMsg(err, "Verification failed"), { description: err?.response?.data?.detail?.attempts_left != null ? `${err.response.data.detail.attempts_left} attempt(s) left` : undefined });
      if (["OTP_EXPIRED", "OTP_LOCKED", "OTP_NOT_REQUESTED"].includes(code)) onBack();
    } finally { setBusy(false); }
  };
  return (
    <Card testId="portal-otp-step">
      <div className="flex items-center gap-3 text-white/85"><LockKeyhole size={18} className="text-[#D4AF37]" /><span className="text-sm">Step 2 · Enter the 6-digit OTP</span></div>
      <p data-testid="portal-otp-sent-to" className="mt-2 text-[12px] text-white/60">Sent to {meta?.mobile_masked} · expires in <span className="text-[#F0D788] tabular-nums">{Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")}</span></p>
      <form onSubmit={submit} className="mt-6 space-y-5">
        <Field label="One-time password">
          <input data-testid="portal-otp-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={otp} placeholder="••••••"
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} className={`${inputCls} text-2xl tracking-[0.5em] text-center`} />
        </Field>
        <div className="flex flex-wrap gap-3">
          <button data-testid="portal-verify-otp" type="submit" disabled={otp.length !== 6 || busy || left === 0} className={goldBtn}>{busy ? "Verifying…" : "Verify & view letters"}</button>
          <button data-testid="portal-resend-otp" type="button" onClick={resend} disabled={busy || cooldown > 0} className="inline-flex items-center min-h-[48px] px-5 rounded-full border border-[#D4AF37]/40 text-[#F0D788] text-sm hover:bg-[#D4AF37]/10 disabled:opacity-40">{cooldown > 0 ? `Resend in ${cooldown}s` : "Resend OTP"}</button>
          <button data-testid="portal-change-mobile" type="button" onClick={onBack} className="inline-flex items-center min-h-[48px] px-5 rounded-full border border-white/20 text-white/80 text-sm hover:border-[#D4AF37]">Change number</button>
        </div>
      </form>
    </Card>
  );
};

const LetterRow = ({ letter }) => {
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    const win = window.open("", "_blank");
    try { const d = await CustomerLetters.link(letter.check_id); if (win) win.location = `${API}${d.path}`; else window.location.assign(`${API}${d.path}`); toast.success("Secure link opened", { description: "Valid for 10 minutes · single use" }); }
    catch (err) { if (win) win.close(); toast.error(errMsg(err, "Could not open letter")); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`portal-letter-${letter.check_id}`} className="rounded-xl border border-white/10 bg-[#0A1F44]/60 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4 hover:border-[#D4AF37]/50 transition-colors">
      <div className="w-11 h-11 shrink-0 rounded-lg bg-[#D4AF37]/15 text-[#D4AF37] inline-flex items-center justify-center"><FileBadge size={20} /></div>
      <div className="min-w-0 flex-1">
        <div className="text-white text-sm font-semibold truncate">Preliminary Loan Eligibility Letter · <span className="text-[#F0D788]">{letter.reference_no}</span></div>
        <div className="text-[12px] text-white/60 mt-1">{LOAN[letter.loan_type] || letter.loan_type || "Loan"} · Version {letter.version_no} · Released {when(letter.released_at)}</div>
      </div>
      <button data-testid={`portal-download-${letter.check_id}`} onClick={open} disabled={busy} className={`${goldBtn} min-h-[44px] px-5`}><Download size={15} />{busy ? "Preparing…" : "View PDF"}</button>
    </div>
  );
};

const LettersStep = ({ onLogout }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => { CustomerLetters.list().then(setData).catch((e) => { if (e?.response?.status === 401) { customerSession.clear(); onLogout(); } else setError(errMsg(e, "Could not load letters")); }); }, [onLogout]);
  return (
    <Card testId="portal-letters-step">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-white/85"><ShieldCheck size={18} className="text-emerald-300" /><span className="text-sm">Verified · {data?.mobile_masked || ""}</span></div>
        <button data-testid="portal-logout" onClick={() => { customerSession.clear(); onLogout(); }} className="inline-flex items-center gap-1.5 text-[12px] text-white/65 hover:text-white"><LogOut size={13} /> Sign out</button>
      </div>
      <div className="mt-6 space-y-3">
        {error && <div data-testid="portal-letters-error" className="text-rose-200 text-sm">{error}</div>}
        {data && data.letters.length === 0 && (
          <div data-testid="portal-no-letters" className="rounded-xl border border-dashed border-white/15 p-6 text-center text-white/65 text-sm">No released eligibility letter is available for this mobile number yet. Letters appear here only after Sahyog Finance has issued them to you.</div>
        )}
        {data?.letters.map((l) => <LetterRow key={l.check_id} letter={l} />)}
        {!data && !error && <div className="text-white/55 text-sm">Loading your letters…</div>}
      </div>
      <p className="mt-6 text-[11px] text-white/70 leading-relaxed">Your session expires after 30 minutes. Each PDF link is valid for 10 minutes and can be opened once — click “View PDF” again for a fresh link.</p>
    </Card>
  );
};

export default function CustomerLetterPortal() {
  const [step, setStep] = useState(() => (customerSession.get() ? "letters" : "mobile"));
  const [mobile, setMobile] = useState("");
  const [meta, setMeta] = useState(null);
  const reset = () => { setStep("mobile"); setMeta(null); };
  return (
    <PolicyPageShell testId="customer-letter-portal" eyebrow="Customer Portal" title="My Eligibility Letter"
      lead="Securely access the Preliminary Loan Eligibility Letter issued to you. Verify with a one-time password sent to your registered mobile.">
      <div className="max-w-xl mx-auto pb-24 sm:pb-8">
        {step === "mobile" && <MobileStep onSent={(m, d) => { setMobile(m); setMeta(d); setStep("otp"); }} />}
        {step === "otp" && <OtpStep mobile={mobile} meta={meta} onVerified={() => setStep("letters")} onBack={reset} onResent={setMeta} />}
        {step === "letters" && <LettersStep onLogout={reset} />}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-[11px] text-white/70">
          {["OTP never shared with anyone", "Only letters issued to you are shown", "Time-limited, single-use PDF links"].map((t) => (
            <div key={t} className="rounded-lg border border-white/10 px-3 py-2 flex items-center gap-2"><ShieldCheck size={12} className="text-[#D4AF37] shrink-0" />{t}</div>
          ))}
        </div>
      </div>
    </PolicyPageShell>
  );
}
